import React from 'react';
import { styled } from '@mui/material';
import { Alert, Row, Stack, Text, Title, PillButton, Card, Link } from 'library/ui';
import { SignUpProps } from 'models';
import ConsentCheckbox from './Consent';
import ReferralCodeInput from './ReferralCodeInput';
import PhoneNumberVerification from './PhoneNumberVerification';
import PersonalInfoSection from './sections/PersonalInfoSection';
import AddressSection from './sections/AddressSection';
import AccountSection from './sections/AccountSection';
import BusinessDetailsSection from './sections/BusinessDetailsSection';
import useSignUpFormState from './useSignUpFormState';

export default function SignupForm({
    location,
    onComplete,
    onClose,
    existingEmail,
    existingPhoneNumber,
    message = 'Create Account',
    redirect = true,
    businessSignup: isBusinessSignup = false,
    actionMessage = 'Sign Up',
    subtitle,
    skipToPhone = false,
    businessType = 'CHILD_CARE',
    hideHeader = false,
    onNotBusinessClick,
}: SignUpProps) {
    const form = useSignUpFormState({
        existingEmail,
        existingPhoneNumber,
        redirect,
        isBusinessSignup,
        businessType,
        location,
        onClose,
        onComplete,
    });

    if (skipToPhone) {
        return (
            <Stack gap="sm">
                {!hideHeader && (
                    <>
                        <Title size="h1">Verify Phone Number</Title>
                        {subtitle && <Text weight="bold">{subtitle}</Text>}
                    </>
                )}
                <PhoneNumberVerification
                    phoneNumber={form.user?.phone_number ?? form.phoneNumber}
                    handleSuccess={form.finallyComplete}
                />
                <FormFooter />
            </Stack>
        );
    }

    function onFormSubmit(event: React.FormEvent) {
        event.preventDefault();
        if (form.buttonDisabled || form.isSubmitting) return;
        form.handlePostUser();
    }

    return (
        <Stack gap="md">
            {!hideHeader && (
                <>
                    <Title size="h1">{message}</Title>
                    {subtitle && <Text weight="bold">{subtitle}</Text>}
                </>
            )}

            <PersonalInfoSection
                firstName={form.firstName}
                setFirstName={form.setFirstName}
                lastName={form.lastName}
                setLastName={form.setLastName}
                phoneDisplay={form.display}
                onPhoneChange={(value, displayPhone) => {
                    form.setPhoneNumber(value);
                    form.setDisplay(displayPhone);
                }}
                phoneError={form.phoneError}
                phoneErrorText={form.phoneErrorText}
                expanded={form.activeSection === 'personal'}
                complete={form.personalComplete}
                onToggle={() => form.toggleSection('personal')}
                onNext={() => form.goToNextFrom('personal')}
            />

            <AddressSection
                setValid={form.setIsAddressValid}
                setAddressPatchBody={form.setAddress}
                expanded={form.activeSection === 'address'}
                complete={form.addressComplete}
                onToggle={() => form.toggleSection('address')}
                onNext={() => form.goToNextFrom('address')}
            />

            <AccountSection
                email={form.email}
                isEmail={form.isEmail}
                onEmailChange={(value, isValid) => {
                    form.setIsEmail(isValid);
                    form.setEmail(value);
                    form.setValid(isValid);
                }}
                password={form.password}
                isPassword={form.isPassword}
                passwordErrorText={form.passwordErrorText}
                onPasswordChange={(value, isValid) => {
                    form.setPassword(value);
                    form.setIsPassword(isValid);
                }}
                expanded={form.activeSection === 'account'}
                complete={form.accountComplete}
                onToggle={() => form.toggleSection('account')}
                onNext={() => form.goToNextFrom('account')}
            />

            {form.showBusiness && (
                <BusinessDetailsSection
                    firstName={form.firstName}
                    state={form.address?.state}
                    isValidProgramId={form.isValidProgramId}
                    onBusinessDataChange={form.setBusinessData}
                    onBusinessErrorChange={form.setBusinessError}
                    onNotBusinessClick={onNotBusinessClick}
                    expanded={form.activeSection === 'business'}
                    complete={form.businessComplete}
                    onToggle={() => form.toggleSection('business')}
                    onNext={() => form.goToNextFrom('business')}
                />
            )}

            <ReviewSection visible={form.activeSection === 'review'}>
                <form onSubmit={onFormSubmit}>
                    <Stack gap="md">
                        <Card tone="muted">
                            <ReferralCodeInput
                                referralCode={form.referralCode}
                                setReferralCode={form.setReferralCode}
                                valid={form.referralCodeValid}
                                setValid={form.setReferralCodeValid}
                            />
                        </Card>

                        <ConsentCheckbox consent={form.consent} setConsent={form.setConsent} />

                        <Row>
                            <PillButton
                                id="signup-submit-button"
                                type="submit"
                                disabled={form.buttonDisabled || form.isSubmitting}
                            >
                                {actionMessage}
                            </PillButton>
                        </Row>

                        <ErrorContainer>{form.error && <Alert variant="error">{form.error}</Alert>}</ErrorContainer>
                    </Stack>
                </form>
            </ReviewSection>

            <FormFooter />
        </Stack>
    );
}

function FormFooter() {
    return (
        <Stack gap="sm" padding="sm">
            <Text size="xs" tone="secondary">
                * Indicates required field
            </Text>
            <Text size="xs" tone="secondary">
                ** Required in the state of Ohio and Illinois
            </Text>
            <Text size="sm" tone="secondary">
                Need help signing up? Email us at{' '}
                <Link href="mailto:support@join-tandem.com" size="sm">
                    support@join-tandem.com
                </Link>
            </Text>
        </Stack>
    );
}

const ReviewSection = styled('div', {
    shouldForwardProp: (prop) => prop !== 'visible',
})<{ visible: boolean }>(({ visible }) => ({
    display: visible ? 'block' : 'none',
    marginTop: 4,
}));

const ErrorContainer = styled('div')({
    minHeight: 48,
});
